import * as _ from "lodash";
import * as angular from "angular";
import { CheckManager } from "../shared/check-manager";
import { Team } from "../models/team.model";
import { TeamService } from "../services/team.service";
import { Invite } from "../models/invite.model";
import { User } from "../models/user.model";
import { Group } from "../models/group.model";

export class TeamDataProvider {
    public observers: any[] = [];
    public allTeamsPromise: Promise<Team[]>;
    public teamData: any = null;
    public searchPromise: any;
    public count: number | null | undefined = null;
    public teams: Team[] | null = null;
    public filteredTeams: Team[] = [];
    public filters: any = {};
    public loadSize = 25;
    public loading = true;
    public error = false;
    public countError = false;
    public scrollLoading = false;

    private $q: any;
    private $timeout: any;
    private $http: any;
    private readonly modelsUtils: any;
    private service: TeamService;
    public checkManager: CheckManager;

    constructor(
        $q: any,
        $timeout: any,
        $http: any,
        ModelsUtils: any,
        TeamService: TeamService
    ) {
        this.$q = $q;
        this.$timeout = $timeout;
        this.$http = $http;
        this.modelsUtils = ModelsUtils;
        this.service = TeamService;
        this.checkManager = new CheckManager(this.filteredTeams);
    }

    notifyObservers(): void {
        for (let i = 0; i < this.observers.length; i++) {
            this.observers[i]();
        }
    }

    addObserver(callback: any): void {
        this.observers.push(callback);
    }

    setFilter(setFilters: object): void {
        this.filters = setFilters;
        this.count = undefined;
        this.filteredTeams.splice(0, this.filteredTeams.length);
        this.getFilteredTeams(null, false);
    }

    /**
     * returns all of the data
     */
    getAllTeams(): Promise<Team[]> {
        if (this.teams !== null) {
            return this.$q.resolve(this.teams);
        } else if (this.allTeamsPromise) {
            //Has been called already and has not resolved yet.
            return this.allTeamsPromise;
        } else {
            const deferred = this.$q.defer();
            this.service.all().then(
                (response: object) => {
                    const json = response["data"];
                    this.teams = [];
                    for (let i = 0; i < json.data.length; i++) {
                        this.teams.push(this.modelsUtils.team(json.data[i]));
                    }
                    deferred.resolve(this.teams);
                },
                (error: object) => {
                    this.teams = [];
                    deferred.reject(error);
                }
            );
            this.allTeamsPromise = deferred.promise;
            return this.allTeamsPromise;
        }
    }

    getFilteredTeams(last: Team, scroll: boolean): Promise<Team[]> {
        if (!last || this.filteredTeams.length < this.count) {
            let returnPromise = this.$q.defer();

            if (scroll) this.scrollLoading = true;
            else this.loading = true;

            if (this.searchPromise)
                this.searchPromise.resolve("user cancelled");
            this.searchPromise = this.$q.defer();

            let filterData = _.cloneDeep(this.filters);
            filterData.limit = this.loadSize;

            this.$http({
                url: "/v2/team/all",
                method: "GET",
                params: {
                    last: last ? { tid: last.tid } : null,
                    filters: filterData,
                },
                timeout: this.searchPromise.promise,
            }).then(
                (response: object) => {
                    const json = response["data"];
                    if (!last) {
                        this.filteredTeams.splice(0, this.filteredTeams.length);
                    }

                    if (json.success) {
                        if (!scroll) {
                            this.getCount();
                        }

                        for (let i = 0; i < json.data.length; i++) {
                            this.filteredTeams.push(
                                this.modelsUtils.team(json.data[i])
                            );
                        }

                        returnPromise.resolve(this.filteredTeams);
                    } else {
                        returnPromise.reject(response);
                    }

                    if (scroll) this.scrollLoading = false;
                    else this.loading = false;
                },
                (error: object) => {
                    if (error["status"] > -1) {
                        this.error = true;

                        if (scroll) this.scrollLoading = false;
                        else this.loading = false;
                    }
                    returnPromise.reject(error);
                }
            );

            return returnPromise.promise;
        }
    }

    getCount(): Promise<number> {
        this.countError = false;
        const deferred = this.$q.defer();

        this.$http({
            url: "/v2/team/count",
            method: "GET",
            params: {
                filters: JSON.stringify(this.filters),
            },
            timeout: this.searchPromise.promise,
        }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.setFilteredTeamsCount(parseInt(json.data));
                    deferred.resolve(parseInt(json.data));
                }
            },
            (error: object) => {
                if (error["status"] > -1) {
                    this.countError = true;
                }
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    bulkDelete(teams: Team[]): Promise<any> {
        const deferred = this.$q.defer();
        this.service.bulkDelete({ tids: teams }).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                this.resetItems();
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    resetItems(): void {
        this.setFilteredTeamsCount(undefined);
        this.checkManager.checkAll(false);
        this.getFilteredTeams(null, false);
    }

    addToTeam(users: User[], team: Team, invites?: Invite[]): Promise<any> {
        const deferred = this.$q.defer();
        this.service
            .addToTeam({ uids: users, inviteIds: invites, tid: team.tid })
            .then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: object) => deferred.reject(error)
            );
        return deferred.promise;
    }

    setFilteredTeamsCount(count: number): void {
        this.count = count;
        this.checkManager.setTotalCount(this.count);
    }

    getDetails(tid: number | string): Promise<any> {
        if (this.teamData !== null) {
            return this.$q.resolve(this.teamData);
        } else {
            const deferred = this.$q.defer();
            this.service.details(tid).then(
                (response: object) => {
                    const json = response["data"];
                    this.teamData = [];
                    this.teamData = json;
                    deferred.resolve(this.teamData);
                },
                (error: object) => {
                    this.teams = [];
                    deferred.reject(error);
                }
            );
            this.allTeamsPromise = deferred.promise;
            return this.allTeamsPromise;
        }
    }

    clearAll(): void {
        this.teams = null;
        this.allTeamsPromise = null;
        this.searchPromise = null;
        this.count = null;
        this.filters = {};
        this.teamData = null;
    }

    teamWithID(tid: number | string): Team {
        if (this.teams && this.teams.length) {
            for (let i = 0; i < this.teams.length; i++) {
                if (this.teams[i].tid == tid) {
                    return this.teams[i];
                }
            }
        }
        return null;
    }

    getTeam(tid: number | string, filters?: object): Promise<Team> {
        const deferred = this.$q.defer();
        this.$http({
            url: "/v2/team/info/" + tid,
            method: "GET",
            params: {
                filters: filters,
            },
        }).then(
            (response: object) => {
                if (response["data"]) {
                    let team = this.modelsUtils.team(response["data"]);
                    deferred.resolve(team);
                }
                deferred.reject();
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    createTeam(team: Team): Promise<Team> {
        const deferred = this.$q.defer();
        this.service.create(team).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    let team = this.modelsUtils.team(json.data);
                    this.replaceTeam(team);
                    deferred.resolve(team);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    updateTeam(team: Team): Promise<Team> {
        const deferred = this.$q.defer();
        this.service.update(team).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    team.setData(json.data);
                    deferred.resolve(team);
                    //This will reset all users teams. Must do after team copy has been extended back into team
                    this.notifyObservers();
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    spliceTeam(team: Team, teams: Team[]): void {
        //Remove team from list
        if (teams !== null) {
            const teamIndex = teams
                .map(function (t) {
                    return t.tid.toString();
                })
                .indexOf(team.tid.toString());
            if (teamIndex > -1) {
                teams.splice(teamIndex, 1);
            }
            this.notifyObservers();
        }
    }

    deleteTeam(team: Team): Promise<Team> {
        const deferred = this.$q.defer();
        this.service.delete({ id: team.tid }).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                this.spliceTeam(team, this.teams);
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    replaceTeam(team: Team): void {
        this.$timeout(() => {
            let existingTeam = this.teamWithID(team.tid);
            if (existingTeam) {
                angular.extend(existingTeam, team);
            } else {
                if (this.teams) this.teams.push(team);
                if (this.filteredTeams) this.filteredTeams.push(team);
            }
            this.notifyObservers();
        });
    }

    getTeamGroups(teamId: string | number): Promise<Group[]> {
        const deferred = this.$q.defer();
        this.service.group(teamId).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    let groups = [];
                    for (let i = 0; i < json.data.length; i++) {
                        groups.push(this.modelsUtils.group(json.data[i]));
                    }
                    deferred.resolve(groups);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getTeamSurveys(teamId: string | number): Promise<any[]> {
        const deferred = this.$q.defer();
        this.service.survey(teamId).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getTeamTalkingPoints(teamId: string | number): Promise<any[]> {
        const deferred = this.$q.defer();
        this.service.talking_point(teamId).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    processSocketUpdate(socketData: any): void {
        if (this.teams) {
            this.replaceTeam(this.modelsUtils.team(socketData));
        }
    }

    processSocketCreate(socketData: any): void {
        if (this.teams) {
            this.replaceTeam(this.modelsUtils.team(socketData));
        }
    }

    processSocketDelete(socketData: any): void {
        if (this.teams) {
            let existingTeam = this.teamWithID(socketData.tid);
            if (existingTeam) {
                this.spliceTeam(existingTeam, this.teams);
            }
        }
    }
}

angular.module("vcDataProvider").service("TeamDataProvider", TeamDataProvider);
